/**
 * One `<note>` element, read into the pieces a document event is made of.
 *
 * This stops short of building a {@link MusicEvent}, because a MusicXML note is not one:
 * the second and later notes of a chord are separate elements that add a pitch to the
 * event the first one started. What comes back here is a flat reading of the element, and
 * the caller decides whether it opens an event or joins the one before it.
 *
 * The written duration is taken from `<type>`, dots and `<time-modification>` whenever the
 * file states them, and only reconstructed from `<duration>` when it does not. A tick
 * count cannot tell a triplet quarter from the tied pair that equals it, so reading the
 * count first would quietly replace the composer's notation with an equivalent one.
 */

import { fromTicks, plain } from "../document/duration";
import type { Duration, NoteType } from "../document/duration";
import { NOTE_TYPES } from "../document/duration";
import type { AccidentalDisplay, NotePitch, Step } from "../document/pitch";
import { STEPS } from "../document/pitch";
import type { TabPosition } from "../document/tab";
import type {
  Arpeggio,
  Articulation,
  BeamState,
  FermataShape,
  Lyric,
  Offset,
  Ornament,
  StemDirection,
} from "../document/types";
import { attr, attrNumber, child, childrenNamed, has, numberOf, textOf } from "../xml/read";
import type { XmlElement } from "../xml/parse";
import type { NoteHead } from "../document/percussion";
import { fromDivisions } from "./common";
import { drumNameOf, headNamed } from "./import-drums";
import type { DrumListing } from "./import-drums";

const ARTICULATIONS: readonly Articulation[] = [
  "staccato",
  "staccatissimo",
  "accent",
  "strong-accent",
  "tenuto",
  "detached-legato",
  "breath-mark",
];

/** The element names MusicXML gives ornaments, against the document's names for them. */
const ORNAMENT_NAMES: Readonly<Record<string, Ornament>> = {
  "trill-mark": "trill",
  turn: "turn",
  "inverted-turn": "inverted-turn",
  "delayed-turn": "delayed-turn",
  mordent: "mordent",
  "inverted-mordent": "inverted-mordent",
  shake: "shake",
  schleifer: "schleifer",
};

const FERMATA_SHAPES: readonly FermataShape[] = ["normal", "angled", "square"];

const ACCIDENTAL_NAMES: Readonly<Record<string, AccidentalDisplay>> = {
  natural: "natural",
  sharp: "sharp",
  flat: "flat",
  "double-sharp": "double-sharp",
  "sharp-sharp": "double-sharp",
  "double-flat": "double-flat",
  "flat-flat": "double-flat",
};

/** A slur end read off a note, still waiting for its other half. */
export interface SlurRead {
  readonly type: "start" | "stop";
  readonly number: number;
  readonly placement: "above" | "below";
}

/**
 * A glissando end read off a note.
 *
 * Unlike a slur it has no placement: the line runs from one note head to the other and there
 * is no side of the staff for it to be on.
 */
export interface GlissRead {
  readonly type: "start" | "stop";
  readonly number: number;
}

/** Everything one `<note>` says, before any of it is turned into an event. */
export interface NoteRead {
  /** This note adds a pitch to the event before it rather than starting one. */
  readonly chord: boolean;
  readonly grace: "acciaccatura" | "appoggiatura" | null;
  readonly rest: boolean;
  /** Printed small: somebody else's music, quoted so the player can find an entry. */
  readonly cue: boolean;
  readonly wholeMeasure: boolean;
  readonly pitch: NotePitch | null;
  /** The head this note is drawn with, which on a drum staff says which drum it is. */
  readonly notehead: NoteHead;
  /** The circle that means a drum was left to ring, as an open hi-hat carries. */
  readonly open: boolean;
  /** Ticks the note occupies; zero for a grace note. */
  readonly ticks: number;
  readonly duration: Duration;
  readonly voice: number;
  /** Zero-based staff index within the part. */
  readonly staff: number;
  readonly stem: StemDirection;
  readonly beam: BeamState;
  readonly articulations: readonly Articulation[];
  readonly ornaments: readonly Ornament[];
  readonly tremolo: number;
  readonly fermata: FermataShape | null;
  /** Moved by hand from where the writing program put it; `null` when it was not. */
  readonly offset: Offset | null;
  readonly arpeggio: Arpeggio | null;
  readonly slurs: readonly SlurRead[];
  readonly glissandos: readonly GlissRead[];
  readonly lyrics: readonly Lyric[];
}

function accidentalOf(note: XmlElement): AccidentalDisplay {
  const element = child(note, "accidental");
  if (element === null) return "auto";
  if (attr(element, "print-object") === "no") return "none";
  return ACCIDENTAL_NAMES[element.text.trim()] ?? "auto";
}

function tiedAs(note: XmlElement, type: "start" | "stop"): boolean {
  const matches = (elements: readonly XmlElement[]): boolean =>
    elements.some((element) => attr(element, "type") === type);
  return (
    matches(childrenNamed(note, "tie")) || matches(childrenNamed(child(note, "notations"), "tied"))
  );
}

function pitchOfNote(note: XmlElement, drums: DrumListing): NotePitch | null {
  const source = child(note, "pitch") ?? child(note, "unpitched");
  if (source === null) return null;
  const step = textOf(source, "step") ?? textOf(source, "display-step") ?? "C";
  const octave = numberOf(source, "octave", numberOf(source, "display-octave", 4));
  const drum = child(note, "unpitched") === null ? null : drumNameOf(note, drums);
  return {
    step: (STEPS.includes(step as Step) ? step : "C") as Step,
    alter: numberOf(source, "alter", 0),
    octave,
    accidental: accidentalOf(note),
    tieStart: tiedAs(note, "start"),
    tieStop: tiedAs(note, "stop"),
    drum,
    tab: tabOf(note),
  };
}

/**
 * Where the note was taken on the instrument, or `null` when the file does not say.
 *
 * Both numbers are needed: a string with no fret says nothing a reader can draw, and a fret
 * with no string could be on any of them. Frets are kept exactly as written, including one
 * that disagrees with the pitch beside it, because which of a guitar's five places a player
 * uses is their decision and not this reader's to correct.
 */
function tabOf(note: XmlElement): TabPosition | null {
  const group = child(child(note, "notations"), "technical");
  if (group === null) return null;
  const string = numberOf(group, "string", 0);
  const fret = textOf(group, "fret") === null ? -1 : numberOf(group, "fret", -1);
  return string < 1 || fret < 0 ? null : { string, fret };
}

function durationOfNote(note: XmlElement, ticks: number): Duration {
  const type = textOf(note, "type");
  const modification = child(note, "time-modification");
  const tuplet =
    modification === null
      ? null
      : {
          actual: numberOf(modification, "actual-notes", 1),
          normal: numberOf(modification, "normal-notes", 1),
        };

  if (type !== null && NOTE_TYPES.includes(type as NoteType)) {
    return { type: type as NoteType, dots: childrenNamed(note, "dot").length, tuplet };
  }
  const recovered = fromTicks(ticks);
  return recovered === null ? plain("quarter") : { ...recovered, tuplet };
}

function beamOf(note: XmlElement): BeamState {
  const first = childrenNamed(note, "beam")[0];
  const value = first?.text.trim();
  if (value === "begin" || value === "continue" || value === "end") return value;
  if (value === "forward hook" || value === "backward hook") return "begin";
  return "auto";
}

function slursOf(note: XmlElement): readonly SlurRead[] {
  return childrenNamed(child(note, "notations"), "slur").map((element) => ({
    type: attr(element, "type") === "stop" ? ("stop" as const) : ("start" as const),
    number: attrNumber(element, "number", 1),
    placement: attr(element, "placement") === "below" ? ("below" as const) : ("above" as const),
  }));
}

/**
 * The glissando ends on a note.
 *
 * Only `<glissando>` is read. `<slide>` is drawn as a straight line and means a portamento,
 * which is a different instruction to a player; reading it as a glissando would write it back
 * out as one and quietly change the music.
 */
function glissandosOf(note: XmlElement): readonly GlissRead[] {
  return childrenNamed(child(note, "notations"), "glissando").map((element) => ({
    type: attr(element, "type") === "stop" ? ("stop" as const) : ("start" as const),
    number: attrNumber(element, "number", 1),
  }));
}

/**
 * Whether the note carries the circle that means the instrument was left to ring.
 *
 * Both spellings are read: `<open/>` is MusicXML 4.0's own, `<open-string/>` is what every
 * drum file written before it carries, and on a drum staff the two mean the same circle.
 */
function openOf(note: XmlElement): boolean {
  const group = child(child(note, "notations"), "technical");
  if (group === null) return false;
  return has(group, "open") || has(group, "open-string");
}

/**
 * MusicXML names an articulation by the element itself, so the child names are the marks.
 *
 * A natural harmonic's circle is kept with them, though the file keeps it among the note's
 * `<technical>` marks. An artificial harmonic is not read: it is two notes and a diamond
 * head, and read as a circle it would be written back as another instruction.
 */
function articulationsOf(note: XmlElement): readonly Articulation[] {
  const notations = child(note, "notations");
  const marks = (child(notations, "articulations")?.children ?? [])
    .map((element) => element.name)
    .filter((name): name is Articulation => ARTICULATIONS.includes(name as Articulation));
  const harmonic = child(child(notations, "technical"), "harmonic");
  return harmonic === null || has(harmonic, "artificial") ? marks : [...marks, "harmonic"];
}

/** The ornaments written above a note, in the order the file wrote them. */
function ornamentsOf(note: XmlElement): readonly Ornament[] {
  const group = child(child(note, "notations"), "ornaments");
  return (group?.children ?? [])
    .map((element) => ORNAMENT_NAMES[element.name])
    .filter((name): name is Ornament => name !== undefined);
}

/**
 * Beams struck through the stem, from the one-note tremolo.
 *
 * A `<tremolo>` with type `start` or `stop` is the two-note kind, which alternates between
 * written notes and is a different sign; it is not read as beams on a stem. The count is
 * capped at three, which is as many as a stem carries.
 */
function tremoloOf(note: XmlElement): number {
  const element = child(child(child(note, "notations"), "ornaments"), "tremolo");
  if (element === null) return 0;
  const type = attr(element, "type") ?? "single";
  if (type !== "single" && type !== "unmeasured") return 0;
  const beams = Number(element.text.trim());
  if (!Number.isInteger(beams) || beams <= 0) return 0;
  return Math.min(beams, 3);
}

/** A fermata's shape is the element's text, `normal` when it says nothing. */
function fermataOf(note: XmlElement): FermataShape | null {
  const element = child(child(note, "notations"), "fermata");
  if (element === null) return null;
  const shape = element.text.trim();
  return FERMATA_SHAPES.includes(shape as FermataShape) ? (shape as FermataShape) : "normal";
}

function arpeggioOf(note: XmlElement): Arpeggio | null {
  const notations = child(note, "notations");
  if (notations === null) return null;
  if (has(notations, "non-arpeggiate")) return "bracket";
  const element = child(notations, "arpeggiate");
  if (element === null) return null;
  return attr(element, "direction") === "down" ? "down" : "up";
}

const SYLLABIC = ["single", "begin", "middle", "end"] as const;

/**
 * The sung text under a note, one entry per `<lyric>`.
 *
 * A verse is numbered by the element's `number`, which the format allows to be any token:
 * where it is not a number -- some writers use `part1verse2` -- the element's position
 * stands in for it, which keeps two verses two verses rather than collapsing them.
 *
 * A syllable split by an elision is several `<text>` children of one `<lyric>`, and they
 * are joined here: the document holds the syllable as it is sung, and the elision itself is
 * a matter of engraving that MusicXML puts back when the file is written again.
 */
function lyricsOf(note: XmlElement): readonly Lyric[] {
  return childrenNamed(note, "lyric").map((element, index) => {
    const syllabic = textOf(element, "syllabic");
    return {
      verse: attrNumber(element, "number", index + 1),
      text: childrenNamed(element, "text")
        .map((part) => part.text.trim())
        .join(""),
      syllabic: SYLLABIC.includes(syllabic as Lyric["syllabic"])
        ? (syllabic as Lyric["syllabic"])
        : "single",
      extend: has(element, "extend"),
    };
  });
}

/**
 * The nudge a `<note>` carries, or `null`.
 *
 * Only `relative-x` and `relative-y` are read. `default-x` and `default-y` look like the
 * same thing and are not: they are the absolute position the program that wrote the file
 * had already worked out for that note, and most programs write them on every note of every
 * bar. Reading them would displace an entire imported score by whatever its old page
 * happened to look like.
 */
function offsetOf(note: XmlElement): Offset | null {
  const x = attrNumber(note, "relative-x", 0);
  const y = attrNumber(note, "relative-y", 0);
  return x === 0 && y === 0 ? null : { x, y };
}

/** Read one `<note>`. `divisions` is whatever was last declared in this part. */
export function readNote(note: XmlElement, divisions: number, drums: DrumListing): NoteRead {
  const grace = child(note, "grace");
  const rest = child(note, "rest");
  const ticks = grace === null ? fromDivisions(numberOf(note, "duration", 0), divisions) : 0;
  const stem = textOf(note, "stem");

  return {
    chord: has(note, "chord"),
    grace: grace === null ? null : attr(grace, "slash") === "yes" ? "acciaccatura" : "appoggiatura",
    rest: rest !== null,
    cue: has(note, "cue"),
    wholeMeasure: rest !== null && attr(rest, "measure") === "yes",
    pitch: rest === null ? pitchOfNote(note, drums) : null,
    notehead: headNamed(textOf(note, "notehead")),
    open: openOf(note),
    ticks,
    duration: durationOfNote(note, ticks),
    voice: numberOf(note, "voice", 1),
    staff: Math.max(0, numberOf(note, "staff", 1) - 1),
    stem: stem === "up" || stem === "down" ? (stem as StemDirection) : "auto",
    beam: beamOf(note),
    articulations: articulationsOf(note),
    ornaments: ornamentsOf(note),
    tremolo: tremoloOf(note),
    fermata: fermataOf(note),
    offset: offsetOf(note),
    arpeggio: arpeggioOf(note),
    slurs: slursOf(note),
    glissandos: glissandosOf(note),
    lyrics: lyricsOf(note),
  };
}
